import type { TFunction } from '@mukil/i18n';
import {
  Button,
  EmptyState,
  GlassCard,
  SkeletonList,
} from '@jumpstone/ui-react';
import type { PayrollSettingsState } from '../../data/use_payroll_settings';

/**
 * Shared non-`ready` rendering for every `usePayrollSettings()` consumer
 * (`data_centre_bank_section.tsx`, `data_centre_loan_section.tsx`,
 * `settings_payroll_section.tsx`) — one copy of the loading/no_entity/
 * entitlement_required/error branches instead of three independent copies
 * that would drift the next time any one of them is edited (see
 * `docs/design-system/CLAUDE.md`'s "one canonical component per concept"
 * rule). Returns `null` for `'ready'` — the caller renders its own real
 * content in that case, this component only ever owns the four non-ready
 * branches.
 */
export function PayrollSettingsStateGate({
  state,
  retry,
  t,
}: {
  state: PayrollSettingsState;
  retry: () => void;
  t: TFunction;
}): React.JSX.Element | null {
  if (state.status === 'loading') {
    return (
      <GlassCard className="rounded-2xl p-5">
        <span className="sr-only">{t('payroll_settings.loading')}</span>
        <SkeletonList rows={3} />
      </GlassCard>
    );
  }
  if (state.status === 'no_entity') {
    return (
      <EmptyState
        title={t('payroll_settings.no_entity_title')}
        description={t('payroll_settings.no_entity_description')}
      />
    );
  }
  if (state.status === 'entitlement_required') {
    return (
      <EmptyState
        title={t('payroll_settings.entitlement_required_title')}
        description={t('payroll_settings.entitlement_required_description')}
      />
    );
  }
  if (state.status === 'error') {
    return (
      <EmptyState
        title={t('payroll_settings.error_title')}
        description={t('payroll_settings.error_description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('payroll_settings.retry')}
          </Button>
        }
      />
    );
  }
  return null;
}
